<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

// Service
import EmployeeNotificationService from '@/api/services/employeeNotification.service';
import DepartmentNotificationService from '@/api/services/departmentNotification.service';

// Icon
import IconBell from '@/components/icons/IconBell.vue';

// My components
import InboxDialog from '@/components/shared/Home/InboxDialog.vue';
import InboxItem from '@/components/shared/Home/InboxItem.vue';
import SharedElementPagination from '@/components/shared/SharedElementPagination.vue';

const authStore = useAuthStore();
const uiStore = useUiStore();

const showDialogInbox = ref(false);
const selectedInbox = ref();
const pinnedInboxItems = ref([]);
const regularInboxItems = ref([]);
const emailRead = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);
const searchText = ref('');
const selectedPriority = ref(null);
const selectedDateRange = ref(null);
const activeMailboxFilter = ref('No leídos');
const mobileFiltersOpen = ref(false);
const isMarkingAllAsRead = ref(false);

const intervalId = ref(null);
const pinnedItemsPerPage = 9999;

const filter = ref([
    { name: "Todos", active: false },
    { name: "No leídos", active: true },
    { name: "Leídos", active: false },
    { name: "Archivados", active: false },
    { name: "Fijados", active: false },
])
const categories = ref([
    { label: 'Formación', value: 'FORMACIÓN' },
    { label: 'Material', value: 'MATERIAL' },
    { label: 'Presencia', value: 'PRESENCIA' },
    { label: 'Proyectos', value: 'PROYECTOS' },
    { label: 'Fondo Social', value: 'social_fund' },
    { label: 'Sala reuniones', value: 'SALA REUNIONES' },
    { label: 'Sugerencias', value: 'SUGERENCIA' },
    { label: 'Ticket', value: 'TICKET' },
    { label: 'Vacaciones', value: 'VACACIONES' },
    { label: 'Vehículos', value: 'VEHÍCULOS' },
])

const selectedCategory = ref(null)
const priorities = ref([
    { label: 'Alta', value: 'high' },
    { label: 'Media', value: 'medium' },
    { label: 'Baja', value: 'low' },
])

const user = computed(() => {
    return authStore.getUserLogged;
})

const currentResultStart = computed(() => {
    if (totalItems.value === 0 || paginatedInboxItems.value.length === 0) {
        return 0;
    }
    return ((currentPage.value - 1) * itemsPerPage.value) + 1;
})

const currentResultEnd = computed(() => {
    return Math.min(currentPage.value * itemsPerPage.value, totalItems.value);
})

const hasInboxActions = (item) => {
    const metadata = item?.actionMetadata;
    const hasActionsArray = metadata?.actions?.length > 0;
    const hasSingleAction = ['action', 'actionType', 'type'].some(key => typeof metadata?.[key] === 'string');

    return Boolean(item?.actionTaken || ((hasActionsArray || hasSingleAction) && !item?.actionTaken));
}

const inboxItems = computed(() => [
    ...pinnedInboxItems.value,
    ...regularInboxItems.value,
])

const paginatedInboxItems = computed(() => {
    return activeMailboxFilter.value === 'Fijados' ? pinnedInboxItems.value : regularInboxItems.value;
})

const hasInboxResults = computed(() => inboxItems.value.length > 0)

const showMarkAllAsReadButton = computed(() => (
    activeMailboxFilter.value === 'No leídos'
    && (totalItems.value > 0 || pinnedInboxItems.value.length > 0)
))

const showActionsColumn = computed(() => inboxItems.value.some(hasInboxActions))

const mobileFilterItems = computed(() => filter.value)

const pinnedCarouselFilters = ['Todos', 'No leídos', 'Leídos', 'Archivados', 'Fijados']

const showPinnedCarousel = computed(() => pinnedCarouselFilters.includes(activeMailboxFilter.value) && pinnedInboxItems.value.length > 0)

const hasSinglePinnedCarouselItem = computed(() => showPinnedCarousel.value && pinnedInboxItems.value.length === 1)

const useCompactPinnedCarousel = computed(() => showPinnedCarousel.value && pinnedInboxItems.value.length > 1)

const shouldShowArchived = computed(() => activeMailboxFilter.value === 'Archivados')

const shouldShowPinnedOnly = computed(() => activeMailboxFilter.value === 'Fijados')

const summaryMessageType = computed(() => shouldShowPinnedOnly.value ? 'mensajes fijados' : 'mensajes no fijados')

const priorityLabelByValue = (value) => {
    return priorities.value.find(priority => priority.value === value)?.label ?? value;
}

const parseDateOnly = (value) => {
    if (!value) {
        return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

const isSameDay = (firstDate, secondDate) => {
    return firstDate?.getFullYear() === secondDate?.getFullYear()
        && firstDate?.getMonth() === secondDate?.getMonth()
        && firstDate?.getDate() === secondDate?.getDate();
}

const formatDateChipPart = (value) => {
    const date = parseDateOnly(value);
    if (!date) {
        return '';
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(date, today)) {
        return 'Hoy';
    }
    if (isSameDay(date, yesterday)) {
        return 'Ayer';
    }

    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

const formatDateChipLabel = (range) => {
    if (!range || range.length !== 2) {
        return '';
    }

    const [fromDate, toDate] = range;
    if (fromDate && toDate && fromDate === toDate) {
        return formatDateChipPart(fromDate);
    }

    return `${formatDateChipPart(fromDate)} - ${formatDateChipPart(toDate)}`;
}

const activeFilterChips = computed(() => {
    const chips = [];

    if (selectedCategory.value && selectedCategory.value !== 'Todos') {
        chips.push({
            key: 'category',
            label: `Categoría: ${categories.value.find(category => category.value === selectedCategory.value)?.label ?? selectedCategory.value}`,
        })
    }

    if (selectedPriority.value && selectedPriority.value !== 'Todos') {
        chips.push({
            key: 'priority',
            label: `Prioridad: ${priorityLabelByValue(selectedPriority.value)}`,
        })
    }

    if (selectedDateRange.value && selectedDateRange.value.length === 2) {
        chips.push({
            key: 'date',
            label: formatDateChipLabel(selectedDateRange.value),
        })
    }

    return chips;
})

const disabledFutureDate = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date.getTime() > today.getTime();
}

const withTime = (date, time) => {
    return date ? `${date}T${time}` : date;
}

onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    if (document.visibilityState === 'visible') {
        startInterVal();
    }
    // getInboxData();
})

onBeforeMount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    stopInterval();
})

onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    stopInterval();
})

const buildInboxParams = () => {
    const nextParams = {
        'order[sentAt]': 'DESC',
        archived: shouldShowArchived.value,
    }

    if (emailRead.value !== null) {
        nextParams['emailRead'] = emailRead.value;
    }
    if (selectedCategory.value && selectedCategory.value !== 'Todos') {
        nextParams['category'] = selectedCategory.value;
    }
    if (searchText.value && searchText.value.trim()) {
        nextParams['search'] = searchText.value.trim();
    }
    if (selectedPriority.value && selectedPriority.value !== 'Todos') {
        nextParams['priority'] = selectedPriority.value;
    }
    if (selectedDateRange.value && selectedDateRange.value.length === 2) {
        nextParams['sentAt[after]'] = withTime(selectedDateRange.value[0], '00:00:00');
        nextParams['sentAt[before]'] = withTime(selectedDateRange.value[1], '23:59:59');
    }

    return nextParams;
}

const getPinnedInboxData = async ({ paginated = false } = {}) => {
    const pinnedParams = {
        ...buildInboxParams(),
        pinned: true,
        page: paginated ? currentPage.value : 1,
        itemsPerPage: paginated ? itemsPerPage.value : pinnedItemsPerPage,
    }

    const res = await EmployeeNotificationService.getAllNotifications(pinnedParams);
    if (res && res.status && res.status === 200) {
        pinnedInboxItems.value = res.data['hydra:member'] ?? [];
        return res.data['hydra:totalItems'] ?? 0;
    }

    pinnedInboxItems.value = [];
    return 0;
}

const getRegularInboxData = async () => {
    const regularParams = {
        ...buildInboxParams(),
        pinned: false,
        page: currentPage.value,
        itemsPerPage: itemsPerPage.value,
    }

    const res = await EmployeeNotificationService.getAllNotifications(regularParams);
    if (res && res.status && res.status === 200) {
        regularInboxItems.value = res.data['hydra:member'] ?? [];
        totalItems.value = res.data['hydra:totalItems'] ?? 0;
        return;
    }

    regularInboxItems.value = [];
    totalItems.value = 0;
}

const getInboxData = async () => {
    if (!user.value) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido el usuario correctamente',
            duration: 6000
        })
        return;
    }

    uiStore.startLoading();
    pinnedInboxItems.value = [];
    regularInboxItems.value = [];
    try {
        if (shouldShowPinnedOnly.value) {
            totalItems.value = await getPinnedInboxData({ paginated: true });
            return;
        }

        await Promise.all([
            getPinnedInboxData(),
            getRegularInboxData(),
        ]);
    } catch (error) {
        console.error('Error cargando bandeja de entrada:', error);
        notification.addNotification({
            type: 'error',
            message: 'Error al cargar la bandeja de entrada',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

const changeFilter = (index) => {
    const item = filter.value[index];

    if (item.active) {
        filter.value.forEach(i => i.active = false);
        emailRead.value = null;
        activeMailboxFilter.value = "Todos";
        const todosIndex = filter.value.findIndex(f => f.name === "Todos");
        if (todosIndex !== -1) {
            filter.value[todosIndex].active = true;
        }
    } else {
        filter.value.forEach((i, idx) => i.active = (idx === index));
        activeMailboxFilter.value = item.name;
        if (item.name === "Leídos") {
            emailRead.value = true;
        } else if (item.name === "No leídos") {
            emailRead.value = false;
        } else {
            emailRead.value = null;
        }
    }

    currentPage.value = 1;
    getInboxData();
}

const changeFilterByName = (name) => {
    const index = filter.value.findIndex(item => item.name === name);
    if (index !== -1) {
        changeFilter(index);
    }
}

const searchInbox = () => {
    currentPage.value = 1;
    getInboxData();
}

const toggleMobileFilters = () => {
    mobileFiltersOpen.value = !mobileFiltersOpen.value;
}

const removeFilterChip = (key) => {
    if (key === 'category') {
        selectedCategory.value = null;
    } else if (key === 'priority') {
        selectedPriority.value = null;
    } else if (key === 'date') {
        selectedDateRange.value = null;
    }

    searchInbox();
}

const resetFilters = () => {
    searchText.value = '';
    selectedCategory.value = null;
    selectedPriority.value = null;
    selectedDateRange.value = null;
    emailRead.value = false;
    activeMailboxFilter.value = 'No leídos';
    filter.value.forEach(item => item.active = item.name === 'No leídos');
    currentPage.value = 1;
    mobileFiltersOpen.value = false;
    getInboxData();
}

const changePagination = () => {
    if (shouldShowPinnedOnly.value) {
        getInboxData();
        return;
    }

    uiStore.startLoading();
    getRegularInboxData()
        .catch((error) => {
            console.error('Error cargando mensajes no fijados:', error);
            notification.addNotification({
                type: 'error',
                message: 'Error al cargar la bandeja de entrada',
                duration: 6000
            })
        })
        .finally(() => {
            uiStore.stopLoading();
        })
}

const openInboxDialog = (selectedMail) => {
    selectedInbox.value = selectedMail
    selectedInbox.value.emailRead = true
    uiStore.startLoading()
    markAsReadItem()
        .then(() => {
            showDialogInbox.value = true
        })
        .catch(error => {
            console.error("Error marcando como leído:", error)
            notification.addNotification({
                title: "Error",
                message: "No se pudo marcar como leído",
                type: "error",
            })
        })
        .finally(() => {
            uiStore.stopLoading()
        })
}

const closeDialogInbox = (read = false) => {
    if (read) {
        getInboxData();
    }
    showDialogInbox.value = false;
    selectedInbox.value = null;
}

const getTitle = (selected) => {
    if (selected && selected.category) {
        switch (selected.category) {
            case 'holiday':
                return 'Vacaciones';
            case 'ticket':
                return 'Tickets';
            case 'presence':
                return 'Presencia';
            case 'training':
                return 'Formación';
            case 'project':
                return 'Proyectos';
            case 'suggestion':
                return 'Sugerencias';
            case 'material':
                return 'Material';
            default:
                return selected.category ? selected.category : '';
        }
    }
    return '';
}

const reloadInbox = () => {
    getInboxData();
}

const startInterVal = () => {
    clearInterval(intervalId.value);
    getInboxData();
    intervalId.value = setInterval(() => {
        getInboxData();
    }, 60_000)
}

const stopInterval = () => {
    clearInterval(intervalId.value)
    intervalId.value = null;
}

const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
        startInterVal();
    } else {
        stopInterval();
    }
}

const markAsReadItem = async () => {
    return markNotificationAsRead(selectedInbox.value);
}

const markNotificationAsRead = async (inboxItem) => {
    if (!inboxItem || !inboxItem['@type']) {
        throw new Error("Tipo de notificación no definido")
    }

    const params = { emailRead: true }

    if (inboxItem['@type'] === "DepartmentNotification") {
        return DepartmentNotificationService.updateDepartmentNotification(
            inboxItem.id,
            params
        )
    } else if (inboxItem['@type'] === "EmployeeNotification") {
        return EmployeeNotificationService.updateEmployeeNotification(
            inboxItem.id,
            params
        )
    }

    throw new Error(`Tipo no soportado: ${inboxItem['@type']}`)
}

const markAllAsRead = async () => {
    if (isMarkingAllAsRead.value) {
        return;
    }

    isMarkingAllAsRead.value = true;
    uiStore.startLoading();

    try {
        const response = await EmployeeNotificationService.markAllNotificationsAsRead();

        if (!response || response.status < 200 || response.status >= 300) {
            throw new Error('No se pudieron marcar los mensajes como leídos');
        }

        await getInboxData();

        const count = response.data?.count ?? 0;

        notification.addNotification({
            type: 'success',
            message: count === 0
                ? 'No había mensajes no leídos'
                : count === 1
                    ? 'El mensaje se ha marcado como leído'
                    : 'Todos los mensajes se han marcado como leídos',
            duration: 4000,
        });
    } catch (error) {
        console.error('Error marcando todos los mensajes como leídos:', error);
        notification.addNotification({
            type: 'error',
            message: 'No se pudieron marcar los mensajes como leídos',
            duration: 6000,
        });
    } finally {
        isMarkingAllAsRead.value = false;
        uiStore.stopLoading();
    }
}
</script>

<template>
    <div id="body-content" class="inbox-body col-12">
        <div class="inbox-header-row">
            <div class="inbox-title-container">
                <IconBell />
                <span id="title">Bandeja de entrada</span>
            </div>
            <div class="inbox-filter-container">
                <div class="filter-options d-flex align-items-center w-100 justify-content-end">
                    <div class="filters inbox-desktop-filters d-flex align-items-center justify-content-end">
                        <template v-for="(item, index) in filter" :key="index">
                            <span class="option" :class="{ active: item.active }" @click="changeFilter(index)">
                                {{ item.name }}
                            </span>
                            <span v-if="index < filter.length - 1" class="separator">|</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div class="inbox-mobile-toolbar">
            <button v-for="item in mobileFilterItems" :key="item.name" class="inbox-mobile-tab" type="button"
                :class="{ active: item.active }" @click="changeFilterByName(item.name)">
                {{ item.name }}
            </button>
        </div>
        <el-card class="inbox-search-card w-100" body-style="padding:0">
            <div class="row inbox-search-row">
                <div v-if="activeFilterChips.length > 0" class="col-12 inbox-mobile-chips">
                    <button v-for="chip in activeFilterChips" :key="chip.key" class="inbox-mobile-chip" type="button"
                        @click="removeFilterChip(chip.key)">
                        {{ chip.label }}
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
                <div class="col-xl-4 col-lg-4 col-md-6 col-12 inbox-search-field inbox-search-field--query">
                    <el-input v-model="searchText" placeholder="Buscar por nombre o asunto" clearable
                        @keyup.enter="searchInbox" @clear="searchInbox">
                        <template #prefix>
                            <font-awesome-icon icon="search" />
                        </template>
                    </el-input>
                    <button class="inbox-mobile-filter-toggle" type="button" :class="{ active: mobileFiltersOpen }"
                        :aria-expanded="mobileFiltersOpen" aria-label="Mostrar filtros" @click="toggleMobileFilters">
                        <font-awesome-icon icon="filter" />
                    </button>
                </div>
                <div class="col-xl-2 col-lg-3 col-md-6 col-12 inbox-search-field inbox-advanced-filter"
                    :class="{ 'is-open': mobileFiltersOpen }">
                    <el-select v-model="selectedCategory" placeholder="Categoría" clearable filterable class="w-100"
                        @change="searchInbox">
                        <el-option v-for="(cat, idx) in categories" :key="idx" :label="cat.label" :value="cat.value" />
                    </el-select>
                </div>
                <div class="col-xl-2 col-lg-2 col-md-6 col-12 inbox-search-field inbox-advanced-filter"
                    :class="{ 'is-open': mobileFiltersOpen }">
                    <el-select v-model="selectedPriority" placeholder="Prioridad" clearable class="w-100"
                        @change="searchInbox">
                        <el-option v-for="(priority, idx) in priorities" :key="idx" :label="priority.label"
                            :value="priority.value" />
                    </el-select>
                </div>
                <div class="col-xl-3 col-lg-3 col-md-6 col-12 inbox-search-field inbox-advanced-filter"
                    :class="{ 'is-open': mobileFiltersOpen }">
                    <div class="form-item date-picker inbox-date-range">
                        <el-date-picker v-model="selectedDateRange" class="w-100" type="daterange" unlink-panels
                            start-placeholder="Desde" end-placeholder="Hasta" format="DD/MM/YYYY"
                            value-format="YYYY-MM-DD" popper-class="inbox-date-range-popper"
                            :disabled-date="disabledFutureDate" @change="searchInbox" />
                    </div>
                </div>
                <div class="col-xl-1 col-lg-12 col-md-12 col-12 inbox-search-actions inbox-advanced-filter"
                    :class="{ 'is-open': mobileFiltersOpen }">
                    <button class="btn btn-link inbox-clear-button" type="button" @click="resetFilters">
                        Limpiar
                        <font-awesome-icon icon="undo" />
                    </button>
                </div>
            </div>
        </el-card>
        <div v-if="totalItems > 0 || showMarkAllAsReadButton" class="inbox-result-actions">
            <div v-if="totalItems > 0" class="inbox-result-summary">
                Mostrando {{ currentResultStart }}-{{ currentResultEnd }} de {{ totalItems }} {{ summaryMessageType }}
            </div>
            <button v-if="showMarkAllAsReadButton" class="inbox-mark-all-read-button" type="button"
                :disabled="isMarkingAllAsRead" :aria-busy="isMarkingAllAsRead" @click="markAllAsRead">
                <font-awesome-icon :icon="isMarkingAllAsRead ? 'spinner' : 'check-circle'"
                    :spin="isMarkingAllAsRead" />
                <span>{{ isMarkingAllAsRead ? 'Marcando mensajes...' : 'Marcar todo como leído' }}</span>
            </button>
        </div>
        <div class="home-cards-container inbox-list" v-if="hasInboxResults">
            <div v-if="pinnedInboxItems.length > 0" class="inbox-section inbox-section--pinned"
                :class="{ 'inbox-section--mobile-carousel': showPinnedCarousel, 'inbox-section--single-mobile-pinned': hasSinglePinnedCarouselItem }">
                <div class="inbox-section-title">
                    <font-awesome-icon icon="thumbtack" />
                    <span class="inbox-section-title-text">
                        {{ showPinnedCarousel ? 'Fijados' : 'Mensajes fijados' }} ({{ pinnedInboxItems.length }})
                    </span>
                </div>
                <div class="inbox-section-items">
                    <div v-for="item in pinnedInboxItems" :key="item.id">
                        <InboxItem :item="item" :show-actions-column="showActionsColumn" @openDialog="openInboxDialog"
                            @reloadInbox="reloadInbox" :compact-pinned-mobile="useCompactPinnedCarousel" />
                    </div>
                </div>
            </div>
            <div v-for="item in regularInboxItems" :key="item.id">
                <InboxItem :item="item" :show-actions-column="showActionsColumn" @openDialog="openInboxDialog"
                    @reloadInbox="reloadInbox" />
            </div>
        </div>
        <div class="empty-state-container" v-else>
            <div class="empty-state-content">
                <p class="empty-title">No hay mensajes en el buzón</p>
                <p class="empty-subtext">Aquí aparecerán tus mensajes cuando lleguen.</p>
            </div>
        </div>
        <div class="mt-3">
            <SharedElementPagination v-model:current-page="currentPage" v-model:page-size="itemsPerPage" size="default"
                :page-size-options="[10, 20, 50]" layout="prev, pager, next, sizes" :total="totalItems"
                @change="changePagination" />
        </div>
        <!-- Dialog inbox open -->
        <el-dialog v-model="showDialogInbox" @close="closeDialogInbox" :show-close="false" width="80%"
            style="max-width: 800px; border-radius: 10px;">
            <InboxDialog v-if="showDialogInbox && selectedInbox" :title="getTitle(selectedInbox)" :mail="selectedInbox"
                @closeDialog="closeDialogInbox" @reloadInbox="reloadInbox" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
#body-content {
    span#title {
        color: $font-blue-color;
        letter-spacing: -0.05em;
        font-size: 2.99em;
        font-weight: bolder;
    }

    #bell {
        fill: $bg-blue;
        width: 45px;
        margin-right: 10px;
        margin-bottom: 4px;
    }
}


.filter-options {
    font-weight: bold;
    flex-wrap: wrap;
    padding-top: 0;
}

.option {
    cursor: pointer;
    color: #3B47FF;
    margin: 0 0.2em;
    font-size: 16px;
}

.option.active {
    color: #FF006E;
}

.separator {
    color: #3B47FF;
    margin: 0 0.2em;
}

.filter-options {
    width: 100%;
}

.inbox-mobile-toolbar,
.inbox-mobile-filter-toggle,
.inbox-mobile-chips {
    display: none;
}

.inbox-title-container {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 0 0 auto;
}

.inbox-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 4px;
    width: 100%;
}

.inbox-filter-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    flex: 1 1 auto;
}

.inbox-search-card {
    border-radius: 12px;
    margin-bottom: 10px;
}

.inbox-search-row {
    align-items: center;
    row-gap: 10px;
    padding: 12px;
}

.inbox-search-field {
    display: flex;
    align-items: center;
    min-width: 0;
}

.inbox-search-field--query {
    gap: 10px;
}

.inbox-date-range {
    width: 100%;
}

.inbox-search-field :deep(.el-input),
.inbox-search-field :deep(.el-select),
.inbox-search-field :deep(.el-date-editor) {
    width: 100%;
}

.inbox-search-field :deep(.el-input__wrapper),
.inbox-search-field :deep(.el-select__wrapper),
.inbox-search-field :deep(.el-date-editor.el-input__wrapper),
.inbox-search-field :deep(.el-date-editor .el-input__wrapper) {
    min-height: 42px;
    border-radius: 20px;
    align-items: center;
}

.inbox-search-field :deep(.el-range-editor.el-input__wrapper) {
    padding: 0 14px;
}

.inbox-search-field :deep(.el-range-input) {
    height: 40px;
    line-height: 40px;
}

.inbox-search-field :deep(.el-range-separator) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    line-height: 40px;
}

.inbox-search-field :deep(.el-input__prefix) {
    left: 16px;
    width: 18px;
    justify-content: center;
}

.inbox-search-field :deep(.el-input__wrapper) {
    padding-left: 14px;
}

.inbox-search-field :deep(.el-input__inner::placeholder) {
    padding-left: 2px;
}

.inbox-search-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 42px;
}

.inbox-clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 42px;
    padding: 0 4px;
    color: $bg-blue;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
}

.inbox-result-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 16px;
}

.inbox-result-summary {
    color: #333;
    font-size: 14px;
    font-weight: 600;
}

.inbox-mark-all-read-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 38px;
    border: 1px solid $bg-blue;
    border-radius: 20px;
    background: $bg-blue;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    line-height: 1;
    padding: 0 14px;
    text-transform: uppercase;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.inbox-mark-all-read-button:hover:not(:disabled),
.inbox-mark-all-read-button:focus-visible {
    background: #fff;
    color: $bg-blue;
    box-shadow: 0 2px 7px rgba(71, 76, 230, 0.25);
}

.inbox-mark-all-read-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.inbox-list {
    overflow-x: hidden;
}

.inbox-section {
    width: 100%;
    margin: 8px 0 14px;
}

.inbox-section--pinned {
    border: 1px solid #f2d36b;
    border-radius: 10px;
    background: #fffdf4;
    box-shadow: 0 2px 8px rgba(71, 76, 230, 0.08);
    overflow: hidden;
}

.inbox-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 34px;
    color: #222;
    background: #fffaf0;
    border-bottom: 1px solid #f2d36b;
    font-size: 14px;
    font-weight: 800;
    padding: 8px 14px;
}

.inbox-section-items {
    background: #fff;
}

.inbox-section-items :deep(.el-row) {
    margin: 0;
}

.inbox-section-items :deep(.inbox-row-card) {
    border-radius: 0 !important;
    border-left: 0;
    border-right: 0;
    border-top: 0;
    box-shadow: none;
}

.inbox-section-items>div:last-child :deep(.inbox-row-card) {
    border-bottom: 0;
}

.empty-state-container {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 0rem 1rem 1rem 1rem;
    width: 100%;
}

.empty-state-content {
    width: 100%;
}

.empty-title {
    font-size: 1.5rem;
    font-weight: bold;
    color: #555;
    margin-bottom: 0.5rem;
}

.empty-subtext {
    color: #777;
    font-size: 1rem;
}

@media (min-width: 768px) {
    .empty-title {
        font-size: 1.75rem;
    }
}

@media (max-width: 720px) {
    .filter-options {
        margin-top: 0;
        justify-content: flex-start !important;
    }

    #body-content {
        #bell {
            margin-bottom: 20px;
        }
    }
}

@media (max-width: 767px) {
    .inbox-body {
        order: 1;
    }

    .inbox-header-row {
        margin-bottom: 8px;
    }

    .inbox-filter-container {
        display: none;
    }

    #body-content {
        span#title {
            font-size: 2.25em;
            line-height: 1;
        }

        #bell {
            width: 34px;
            margin-right: 8px;
            margin-bottom: 0 !important;
        }
    }

    .inbox-mobile-toolbar {
        display: grid;
        grid-template-columns: 0.82fr 1.08fr 0.88fr 1.24fr 0.98fr;
        align-items: center;
        gap: 4px;
        width: 100%;
        overflow-x: hidden;
        padding: 8px 6px;
        margin-bottom: 10px;
        background: $bg-blue;
        border-radius: 14px;
        box-shadow: 0 2px 8px rgba(71, 76, 230, 0.16);
    }

    .inbox-mobile-tab {
        min-width: 0;
        min-height: 38px;
        border: 0;
        border-radius: 10px;
        background: transparent;
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        line-height: 1;
        padding: 0 1px;
        overflow: hidden;
        text-overflow: clip;
        white-space: nowrap;
    }

    .inbox-mobile-tab.active {
        background: #fff;
        color: $bg-blue;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }

    .inbox-search-card {
        border-radius: 14px;
        margin-bottom: 12px;
    }

    .inbox-search-row {
        padding: 12px;
        row-gap: 10px;
    }

    .inbox-search-field--query {
        display: flex;
        gap: 8px;
    }

    .inbox-search-field--query :deep(.el-input) {
        flex: 1 1 auto;
        min-width: 0;
    }

    .inbox-mobile-filter-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 42px;
        width: 42px;
        height: 42px;
        border: 1px solid #d7daf8;
        border-radius: 50%;
        background: #fff;
        color: $bg-blue;
        font-size: 18px;
    }

    .inbox-mobile-filter-toggle.active {
        background: $bg-blue;
        color: #fff;
        border-color: $bg-blue;
    }

    .inbox-mobile-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        overflow-x: visible;
        padding-bottom: 2px;
    }

    .inbox-mobile-chip {
        display: inline-flex;
        align-items: flex-start;
        gap: 6px;
        flex: 0 1 auto;
        max-width: 100%;
        min-height: 32px;
        border: 1px solid #c8ccff;
        border-radius: 16px;
        background: #fff;
        color: $bg-blue;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.25;
        padding: 7px 10px;
        text-align: left;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .inbox-mobile-chip span {
        flex: 0 0 auto;
        line-height: 1;
        margin-top: 1px;
    }

    .inbox-advanced-filter {
        display: none;
    }

    .inbox-advanced-filter.is-open {
        display: flex;
    }

    .inbox-advanced-filter.is-open.inbox-search-actions {
        display: flex;
        justify-content: space-between;
        border-top: 1px solid #eef0fb;
        padding-top: 10px;
    }

    .inbox-search-actions {
        justify-content: flex-start;
    }

    .filters {
        justify-content: flex-start !important;
    }

    .inbox-result-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 8px;
        margin: 8px 0;
        padding: 0 2px;
    }

    .inbox-result-summary {
        padding: 0 2px;
        font-size: 13px;
    }

    .inbox-mark-all-read-button {
        width: 100%;
    }

    .inbox-section {
        margin: 10px 0 14px;
    }

    .inbox-section-title {
        min-height: 38px;
        font-size: 13px;
    }

    .inbox-section--mobile-carousel {
        border: 0;
        background: transparent;
        box-shadow: none;
        overflow: visible;
    }

    .inbox-section--mobile-carousel .inbox-section-title {
        justify-content: flex-start;
        border: 0;
        background: transparent;
        padding: 4px 2px 8px;
    }

    .inbox-section--mobile-carousel .inbox-section-items {
        display: flex;
        gap: 10px;
        overflow-x: auto;
        padding: 0 2px 6px;
        background: transparent;
        scroll-snap-type: x proximity;
    }

    .inbox-section--mobile-carousel .inbox-section-items>div {
        flex: 0 0 min(75vw, 340px);
        scroll-snap-align: start;
    }

    .inbox-section--single-mobile-pinned .inbox-section-items {
        display: block;
        overflow-x: visible;
        padding: 0;
        scroll-snap-type: none;
    }

    .inbox-section--single-mobile-pinned .inbox-section-items>div {
        width: 100%;
    }

    .inbox-section--mobile-carousel .inbox-section-items :deep(.inbox-row-card) {
        border: 1px solid #f2d36b;
        border-radius: 12px !important;
        box-shadow: 0 2px 8px rgba(71, 76, 230, 0.08);
    }
}

:global(.inbox-date-range-popper) {
    max-width: calc(100vw - 24px);
}

@media (max-width: 767px) {
    :global(.inbox-date-range-popper.el-picker__popper) {
        left: 12px !important;
        right: 12px !important;
        width: calc(100vw - 24px);
    }

    :global(.inbox-date-range-popper .el-date-range-picker) {
        width: 100%;
        max-width: calc(100vw - 24px);
    }

    :global(.inbox-date-range-popper .el-picker-panel__body) {
        min-width: 0;
        width: 100%;
    }

    :global(.inbox-date-range-popper .el-date-range-picker__content) {
        width: 100%;
        padding: 12px;
    }

    :global(.inbox-date-range-popper .el-date-range-picker__content.is-right) {
        display: none;
    }

    :global(.inbox-date-range-popper .el-date-range-picker__content.is-left) {
        border-right: 0;
    }
}

@media (max-width: 1200px) {
    #bell {
        margin-bottom: 14px !important;
    }
}
</style>
